'use client'

import { RefCallback, useRef } from 'react'
import { FlatListProps, ItemWithId } from './types'

export default function useListScrollTracking<Item extends ItemWithId>({
    isFetching,
    hasMoreData,
    onScrollToEnd,
    scrollToEndThreshold = 0.25,
}: {
    isFetching: FlatListProps<Item>['isFetching']
    hasMoreData: FlatListProps<Item>['hasMoreData']
    onScrollToEnd: FlatListProps<Item>['onScrollToEnd']
    scrollToEndThreshold: FlatListProps<Item>['scrollToEndThreshold']
}): RefCallback<HTMLElement> {
    const observer = useRef<IntersectionObserver | null>(null)

    return (node: HTMLElement) => {
        if (isFetching) {
            return
        }

        if (observer.current) {
            observer.current.disconnect()
        }

        if (typeof IntersectionObserver === 'undefined') {
            // This can happen e.g. when running Jest
            return
        }

        observer.current = new IntersectionObserver(
            (entries) => {
                if (onScrollToEnd && hasMoreData && entries[0]?.isIntersecting) {
                    onScrollToEnd()
                }
            },
            {
                threshold: scrollToEndThreshold,
            }
        )

        if (node) {
            observer.current.observe(node)
        }
    }
}
